import { QueryKey, useQueryClient } from '@tanstack/react-query';
import { useEventCallback } from '@mui/material';

import { AppIssue, IssuesResponse, AppIssueBrief } from '@playq/services-beetle';
import { Environment } from '@playq/octopus-common';

import { useUpdateStatus } from '/api';
import { snackbarService } from '/common/snackbarService';
import { issueKeys } from '/component/Support/BeetlesLookup/constants';

import { SupportContentType } from '../types';

interface UseOptimisticUpdateStatusProps {
  issue: AppIssue;
  keys: QueryKey;
  env: Environment;
  type: SupportContentType;
}
export const useOptimisticUpdateIssueStatus = ({ issue, keys, env, type }: UseOptimisticUpdateStatusProps) => {
  const queryClient = useQueryClient();

  const { mutate: updateStatus, isPending: isUpdateLoading } = useUpdateStatus(type, {
    onMutate: async (newData) => {
      await queryClient.cancelQueries({ queryKey: keys });
      const previousData = queryClient.getQueryData<AppIssue>(keys);

      if (previousData) {
        queryClient.setQueryData<AppIssue>(keys, (oldData) => {
          if (!oldData) return undefined;

          const newIssue = new AppIssue(oldData.serialize());
          newIssue.resolved = Boolean(newData.status);
          return newIssue;
        });
      }

      return { previousData };
    },

    onError: (_error, _variables, context) => {
      const ctx = context as { previousData?: AppIssue };
      if (ctx.previousData != null) {
        queryClient.setQueryData(keys, ctx.previousData);
      }
      snackbarService.error('Failed to update status.');
    },

    onSuccess: (_, variables) => {
      queryClient.setQueriesData({ queryKey: issueKeys }, (oldResponse: IssuesResponse | undefined) => {
        if (oldResponse === undefined) return undefined;

        const newArray = oldResponse.issues.map((item) => {
          if (item.id.id === variables.id.id) {
            const newIssue = new AppIssueBrief(item.serialize());
            newIssue.resolved = Boolean(variables.status);
            return newIssue;
          }
          return item;
        });

        const newResponse = new IssuesResponse(oldResponse.serialize());
        newResponse.issues = newArray;
        return newResponse;
      });

      snackbarService.success(`Issue status was changed`);
    },
  });

  const handleUpdateClick = useEventCallback(() => {
    updateStatus({ id: issue.id, status: !issue.resolved, env });
  });

  return {
    handleUpdateClick,
    isUpdateLoading,
    buttonText: issue.resolved ? 'Unresolve' : 'Resolve',
  };
};
